import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { toast } from 'react-hot-toast';
import { formatearRut } from '../utils/rutValidator';
import { tratamientosService } from '../api/tratamientos';
import ResponsiveTable from '../components/common/ResponsiveTable';
import AddButton from '../components/ui/AddButton';
import { 
  Building2, 
  Image, 
  Clock, 
  Globe, 
  Save,
  Upload,
  Loader2,
  Settings,
  User,
  Award,
  IdCard,
  Shield,
  MapPin,
  Phone,
  Mail,
  Copy,
  Check,
  Stethoscope
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const ConfiguracionClinicaPage = () => {
  const { currentUser, refreshUser } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('clinica');
  const [clinicSlug, setClinicSlug] = useState('');
  const [copiedBookingLink, setCopiedBookingLink] = useState(false);
  const [weekendAvailability, setWeekendAvailability] = useState({
    saturday: false,
    sunday: false,
  });
  
  // Clinic form state
  const [formData, setFormData] = useState({
    nombre: '',
    ciudad: '',
    especialidad: 'podologia',
    hora_apertura: '09:00',
    hora_cierre: '19:00',
    acepta_reservas_online: false,
    dias_atencion: '0,1,2,3,4'
  });
  
  // Professional profile state
  const [profileData, setProfileData] = useState({
    titulo_profesional: '',
    rut_profesional: '',
    rol_minsal: '',
    direccion_consulta: '',
    ciudad_consulta: ''
  });
  const [profileCountry, setProfileCountry] = useState('CL');
  
  // Logo states
  const [currentLogo, setCurrentLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  // Tratamientos state
  const [tratamientos, setTratamientos] = useState([]);
  const [filteredTratamientos, setFilteredTratamientos] = useState([]);
  const [loadingTratamientos, setLoadingTratamientos] = useState(true);
  const [showTratamientoModal, setShowTratamientoModal] = useState(false);
  const [editingTratamiento, setEditingTratamiento] = useState(null);
  const [tratamientoFormData, setTratamientoFormData] = useState({
    nombre: '',
    precio: '',
    duracion_minutos: '60',
    descripcion: ''
  });
  const [tratamientoFormError, setTratamientoFormError] = useState('');

  // Helper para URL del logo
  const getLogoUrl = (logoPath) => {
    if (!logoPath) return null;
    if (logoPath.startsWith('data:')) return logoPath; // Base64 (preview)
    if (logoPath.startsWith('http')) return logoPath; // Absolute URL
    
    // URL Base del backend
    const API_BASE_URL = process.env.REACT_APP_API_URL 
      ? process.env.REACT_APP_API_URL.replace('/api', '') 
      : 'http://localhost:8000';
      
    // Asegurar que no haya doble barra
    const cleanPath = logoPath.startsWith('/') ? logoPath.substring(1) : logoPath;
    return `${API_BASE_URL}/${cleanPath}`;
  };

  const getBookingLink = () => {
    if (!clinicSlug) return '';
    const landingBaseUrl = (process.env.REACT_APP_LANDING_URL || 'https://www.estetikflow.cl').replace(/\/$/, '');
    return `${landingBaseUrl}/reservar/${clinicSlug}`;
  };

  const parseDiasAtencion = (diasAtencionValue) => {
    const parsedDays = (diasAtencionValue || '0,1,2,3,4')
      .split(',')
      .map((day) => Number(day.trim()))
      .filter((day) => !Number.isNaN(day));

    return {
      saturday: parsedDays.includes(5),
      sunday: parsedDays.includes(6),
    };
  };

  const buildDiasAtencion = ({ saturday, sunday }) => {
    const baseDays = [0, 1, 2, 3, 4]; // Lunes a Viernes
    if (saturday) baseDays.push(5);
    if (sunday) baseDays.push(6);
    return baseDays.join(',');
  };

  const getActiveDaysLabel = () => {
    const extras = [];
    if (weekendAvailability.saturday) extras.push('Sábado');
    if (weekendAvailability.sunday) extras.push('Domingo');

    return extras.length
      ? `Días activos: Lunes a Viernes + ${extras.join(' + ')}`
      : 'Días activos: Lunes a Viernes';
  };
  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (currentUser) {
      setProfileData({
        titulo_profesional: currentUser.titulo_profesional || 'Podólogo/a Clínico/a',
        rut_profesional: currentUser.rut_profesional || '',
        rol_minsal: currentUser.rol_minsal || '',
        direccion_consulta: currentUser.direccion_consulta || '',
        ciudad_consulta: currentUser.ciudad_consulta || ''
      });
    }
  }, [currentUser]);

  const fetchAllData = async () => {
    try {
      setIsLoading(true);
      const [clinicRes, tratamientosRes] = await Promise.all([
        api.get('/cuentas/mi-cuenta/'),
        tratamientosService.getAll()
      ]);
      
      const clinicData = clinicRes.data;
      setFormData({
        nombre: clinicData.nombre || '',
        ciudad: clinicData.ciudad || '',
        especialidad: clinicData.especialidad || 'podologia',
        hora_apertura: clinicData.hora_apertura || '09:00',
        hora_cierre: clinicData.hora_cierre || '19:00',
        acepta_reservas_online: clinicData.acepta_reservas_online || false,
        dias_atencion: clinicData.dias_atencion || '0,1,2,3,4'
      });
      setWeekendAvailability(parseDiasAtencion(clinicData.dias_atencion));
      setClinicSlug(clinicData.slug || '');
      
      if (clinicData.logo) {
        setCurrentLogo(clinicData.logo);
      }

      setTratamientos(Array.isArray(tratamientosRes.data) ? tratamientosRes.data : []);
      
      // Filtrar tratamientos por especialidad de la clínica
      const allTratamientos = Array.isArray(tratamientosRes.data) ? tratamientosRes.data : [];
      const especialidadClinica = clinicData.especialidad;
      if (especialidadClinica && especialidadClinica !== 'multi') {
        setFilteredTratamientos(allTratamientos.filter(t => !t.especialidad || t.especialidad === '' || t.especialidad === especialidadClinica));
      } else {
        setFilteredTratamientos(allTratamientos);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar la configuración');
    } finally {
      setIsLoading(false);
      setLoadingTratamientos(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    if (name === 'rut_profesional' && profileCountry === 'CL') {
      setProfileData(prev => ({ ...prev, [name]: formatearRut(value) }));
    } else {
      setProfileData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Por favor selecciona un archivo de imagen válido');
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        toast.error('La imagen no debe superar los 2MB');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmitClinica = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const diasAtencion = buildDiasAtencion(weekendAvailability);

      const clinicData = new FormData();
      clinicData.append('nombre', formData.nombre);
      clinicData.append('ciudad', formData.ciudad);
      clinicData.append('especialidad', formData.especialidad);
      clinicData.append('hora_apertura', formData.hora_apertura);
      clinicData.append('hora_cierre', formData.hora_cierre);
      clinicData.append('acepta_reservas_online', formData.acepta_reservas_online);
      clinicData.append('dias_atencion', diasAtencion);
      if (logoFile) clinicData.append('logo', logoFile);

      const response = await api.patch('/cuentas/mi-cuenta/', clinicData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setClinicSlug(response.data?.slug || clinicSlug);
      setFormData(prev => ({ ...prev, dias_atencion: diasAtencion }));

      toast.success('Configuración de clínica guardada');
      await refreshUser();
      if (logoFile) {
        setCurrentLogo(logoPreview);
        setLogoPreview(null);
        setLogoFile(null);
      }
    } catch (error) {
      console.error('Error saving clinic config:', error);
      toast.error('Error al guardar la configuración');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyBookingLink = async () => {
    const bookingLink = getBookingLink();
    if (!bookingLink) {
      toast.error('No se pudo generar el enlace de reservas todavía');
      return;
    }

    try {
      await navigator.clipboard.writeText(bookingLink);
      setCopiedBookingLink(true);
      toast.success('Enlace copiado al portapapeles');
      setTimeout(() => setCopiedBookingLink(false), 2000);
    } catch (error) {
      toast.error('No se pudo copiar el enlace');
    }
  };

  const handleSubmitPerfil = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await api.patch('/usuarios/me/', profileData);
      toast.success('Perfil profesional actualizado');
      await refreshUser();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Error al guardar el perfil');
    } finally {
      setIsSaving(false);
    }
  };

  // --- Tratamientos handlers ---
  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return new Intl.NumberFormat('es-CL', {
      style: 'currency', currency: 'CLP', minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(value);
  };

  const handleOpenTratamientoModal = (tratamiento = null) => {
    if (tratamiento) {
      setEditingTratamiento(tratamiento);
      setTratamientoFormData({
        nombre: tratamiento.nombre || '',
        precio: tratamiento.precio?.toString() || '',
        duracion_minutos: tratamiento.duracion_minutos?.toString() || '60',
        descripcion: tratamiento.descripcion || ''
      });
    } else {
      setEditingTratamiento(null);
      setTratamientoFormData({ nombre: '', precio: '', duracion_minutos: '60', descripcion: '' });
    }
    setTratamientoFormError('');
    setShowTratamientoModal(true);
  };

  const handleCloseTratamientoModal = () => {
    setShowTratamientoModal(false);
    setEditingTratamiento(null);
    setTratamientoFormError('');
  };

  const handleSubmitTratamiento = async (e) => {
    e.preventDefault();
    setTratamientoFormError('');

    if (!tratamientoFormData.nombre.trim()) return setTratamientoFormError('El nombre es obligatorio');
    if (!tratamientoFormData.precio || parseFloat(tratamientoFormData.precio) < 0) return setTratamientoFormError('El precio debe ser válido');
    if (!tratamientoFormData.duracion_minutos || parseInt(tratamientoFormData.duracion_minutos) <= 0) return setTratamientoFormError('La duración debe ser mayor a 0');

    try {
      const data = {
        nombre: tratamientoFormData.nombre.trim(),
        precio: parseFloat(tratamientoFormData.precio),
        duracion_minutos: parseInt(tratamientoFormData.duracion_minutos),
        descripcion: tratamientoFormData.descripcion.trim() || null
      };

      if (editingTratamiento) {
        await tratamientosService.update(editingTratamiento.id, data);
        toast.success('Tratamiento actualizado');
      } else {
        await tratamientosService.create(data);
        toast.success('Tratamiento creado');
      }
      handleCloseTratamientoModal();
      const res = await tratamientosService.getAll();
      const allTratamientos = Array.isArray(res.data) ? res.data : [];
      setTratamientos(allTratamientos);
      // Re-filter by especialidad
      const esp = formData.especialidad;
      if (esp && esp !== 'multi') {
        setFilteredTratamientos(allTratamientos.filter(t => !t.especialidad || t.especialidad === '' || t.especialidad === esp));
      } else {
        setFilteredTratamientos(allTratamientos);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al guardar el tratamiento';
      setTratamientoFormError(errorMsg);
    }
  };

  const handleDeleteTratamiento = async (id) => {
    if (window.confirm('¿Eliminar este tratamiento?')) {
      try {
        await tratamientosService.delete(id);
        toast.success('Tratamiento eliminado');
        const res = await tratamientosService.getAll();
        const allTratamientos = Array.isArray(res.data) ? res.data : [];
        setTratamientos(allTratamientos);
        const esp = formData.especialidad;
        if (esp && esp !== 'multi') {
          setFilteredTratamientos(allTratamientos.filter(t => !t.especialidad || t.especialidad === '' || t.especialidad === esp));
        } else {
          setFilteredTratamientos(allTratamientos);
        }
      } catch (error) {
        toast.error('No se puede eliminar el tratamiento');
      }
    }
  };

  const especialidadBadge = (esp) => {
    if (!esp) return <span className="text-xs text-gray-400">—</span>;
    const colors = {
      podologia: 'bg-blue-100 text-blue-800',
      cosmetologia: 'bg-pink-100 text-pink-800',
      manicuria: 'bg-purple-100 text-purple-800',
    };
    const labels = {
      podologia: '🦶 Podología',
      cosmetologia: '✨ Cosmetología',
      manicuria: '💅 Manicuría',
    };
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors[esp] || 'bg-gray-100 text-gray-600'}`}>
        {labels[esp] || esp}
      </span>
    );
  };

  const tratamientoColumns = [
    { key: 'nombre', label: 'Nombre', render: (v) => <span className="font-medium text-gray-900">{v}</span> },
    { key: 'especialidad', label: 'Especialidad', render: (v, item) => especialidadBadge(v || item?.especialidad) },
    { key: 'precio', label: 'Precio', render: (v) => <span className="font-semibold text-green-600">{formatCurrency(v)}</span> },
    { key: 'duracion_minutos', label: 'Duración', render: (v) => <span className="text-gray-700">{v} min</span> }
  ];

  // --- Tabs ---
  const tabs = [
    { key: 'clinica', label: '🏢 Mi Clínica' },
    { key: 'perfil', label: '👤 Mi Perfil' },
    { key: 'servicios', label: '🩺 Servicios' }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#2A9D8F]" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Settings className="w-7 h-7 text-[#2A9D8F]" />
          Configuración
        </h1>
        <p className="text-gray-500 mt-1">Gestiona tu clínica, perfil profesional y catálogo de servicios.</p>
      </div>

      {/* Tabs Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-1 sm:gap-2" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap
                ${activeTab === tab.key 
                  ? 'border-b-2 border-[#2A9D8F] text-[#2A9D8F] font-semibold' 
                  : 'text-gray-500 hover:text-gray-700 hover:border-b-2 hover:border-gray-300'
                }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {/* ===== TAB: MI CLÍNICA ===== */}
        {activeTab === 'clinica' && (
          <form onSubmit={handleSubmitClinica} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Columna 1: Identidad Corporativa */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Building2 className="w-5 h-5 mr-2 text-[#2A9D8F]" />
                    Identidad Corporativa
                  </h2>
                </div>
                <div className="p-6 space-y-5">
                  <div>
                    <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-1">Nombre de la Clínica</label>
                    <input
                      type="text" id="nombre" name="nombre" value={formData.nombre} onChange={handleInputChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] bg-gray-50"
                      placeholder="Ej: Clínica Podológica Esmeralda"
                    />
                  </div>
                  <div>
                    <label htmlFor="especialidad" className="block text-sm font-medium text-gray-700 mb-1">Rubro o Especialidad</label>
                    <select
                      id="especialidad" name="especialidad" value={formData.especialidad} onChange={handleInputChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] bg-white"
                    >
                      <option value="podologia">🦶 Podología</option>
                      <option value="cosmetologia">✨ Cosmetología</option>
                      <option value="manicuria">💅 Manicuría</option>
                      <option value="multi">🏥 Multi-especialidad</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="ciudad" className="block text-sm font-medium text-gray-700 mb-1">Ciudad de la Clínica</label>
                    <input
                      type="text" id="ciudad" name="ciudad" value={formData.ciudad} onChange={handleInputChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] bg-gray-50"
                      placeholder="Ej: Santiago, Valparaíso, Concepción..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Logo de la Clínica</label>
                    <div className="flex items-start gap-4">
                      <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center bg-gray-50 overflow-hidden flex-shrink-0">
                        {logoPreview ? (
                          <img src={logoPreview} alt="Preview" className="w-full h-full object-contain" />
                        ) : currentLogo ? (
                          <img 
                            src={getLogoUrl(currentLogo)} 
                            alt="Logo" 
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              e.target.onerror = null; 
                              e.target.style.display = 'none'; // Ocultar si falla
                            }}
                          />
                        ) : (
                          <Image className="w-8 h-8 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <label htmlFor="logo-upload" className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
                          <Upload className="w-4 h-4 mr-2" /> Subir
                        </label>
                        <input type="file" id="logo-upload" accept="image/*" onChange={handleLogoChange} className="hidden" />
                        <p className="mt-2 text-xs text-gray-500">PNG, JPG, WEBP. Max 2MB.</p>
                        {logoPreview && <button type="button" onClick={() => { setLogoPreview(null); setLogoFile(null); }} className="text-xs text-red-600">Cancelar</button>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Columna 2: Configuración Operativa */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Clock className="w-5 h-5 mr-2 text-[#2A9D8F]" />
                    Configuración Operativa
                  </h2>
                </div>
                <div className="p-6 space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Horario de Atención</label>
                    <div className="flex items-center gap-3">
                      <input type="time" name="hora_apertura" value={formData.hora_apertura} onChange={handleInputChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] bg-gray-50" />
                      <span className="text-gray-400">a</span>
                      <input type="time" name="hora_cierre" value={formData.hora_cierre} onChange={handleInputChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] bg-gray-50" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <div className="flex items-start gap-3">
                      <Globe className="w-5 h-5 text-[#2A9D8F] mt-0.5" />
                      <div>
                        <span className="text-sm font-medium text-gray-900">Reservas Online</span>
                        <p className="text-xs text-gray-500">Permitir reservas desde web pública</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" name="acepta_reservas_online" checked={formData.acepta_reservas_online} onChange={handleInputChange} className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-[#2A9D8F]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2A9D8F]"></div>
                    </label>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
                    <p className="text-sm font-medium text-gray-900">Días adicionales de atención</p>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={weekendAvailability.saturday}
                          onChange={(e) => setWeekendAvailability((prev) => ({ ...prev, saturday: e.target.checked }))}
                          className="h-4 w-4 rounded border-gray-300 text-[#2A9D8F] focus:ring-[#2A9D8F]"
                        />
                        Atiende sábado
                      </label>
                      <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={weekendAvailability.sunday}
                          onChange={(e) => setWeekendAvailability((prev) => ({ ...prev, sunday: e.target.checked }))}
                          className="h-4 w-4 rounded border-gray-300 text-[#2A9D8F] focus:ring-[#2A9D8F]"
                        />
                        Atiende domingo
                      </label>
                    </div>
                    <p className="text-xs text-gray-500">
                      Lunes a viernes permanecen habilitados por defecto. Guarda para aplicar cambios en reservas online.
                    </p>
                    <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-[#2A9D8F]/10 text-[#1f6f66] text-xs font-medium border border-[#2A9D8F]/20">
                      {getActiveDaysLabel()}
                    </div>
                  </div>

                  {formData.acepta_reservas_online && (
                    <div className="p-4 bg-[#2A9D8F]/5 rounded-xl border border-[#2A9D8F]/20 space-y-3">
                      <p className="text-sm font-medium text-gray-800">Link público para reservas</p>
                      {clinicSlug ? (
                        <div className="space-y-2">
                          <div className="flex flex-col sm:flex-row gap-2">
                            <input
                              type="text"
                              readOnly
                              value={getBookingLink()}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-700"
                            />
                            <button
                              type="button"
                              onClick={handleCopyBookingLink}
                              className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-[#2A9D8F] text-white text-sm font-medium hover:bg-[#21867a] transition-colors whitespace-nowrap"
                            >
                              {copiedBookingLink ? (
                                <>
                                  <Check className="w-4 h-4 mr-2" />
                                  Copiado
                                </>
                              ) : (
                                <>
                                  <Copy className="w-4 h-4 mr-2" />
                                  Copiar Link
                                </>
                              )}
                            </button>
                          </div>
                          <a
                            href={getBookingLink()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block text-sm text-[#2A9D8F] hover:text-[#21867a] underline"
                          >
                            Abrir enlace de reservas
                          </a>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-600">
                          Guarda la configuración para generar el enlace público de reservas.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Botón Guardar */}
            <div className="flex justify-end">
              <button type="submit" disabled={isSaving}
                className="inline-flex items-center px-6 py-3 rounded-xl text-sm font-medium text-white bg-[#2A9D8F] hover:bg-[#21867a] disabled:opacity-50 transition-colors">
                {isSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</> : <><Save className="w-4 h-4 mr-2" />Guardar Clínica</>}
              </button>
            </div>
          </form>
        )}

        {/* ===== TAB: MI PERFIL ===== */}
        {activeTab === 'perfil' && (
          <form onSubmit={handleSubmitPerfil} className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <User className="w-5 h-5 mr-2 text-[#2A9D8F]" />
                  Perfil Profesional
                </h2>
                <p className="text-xs text-gray-500 mt-1">Estos datos aparecerán en certificados y documentos.</p>
              </div>
              <div className="p-6 space-y-5">
                {/* Título */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Título Profesional</label>
                  <div className="relative">
                    <Award className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input type="text" name="titulo_profesional" value={profileData.titulo_profesional} onChange={handleProfileChange}
                      className="w-full pl-10 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] bg-gray-50" placeholder="Podóloga Clínica" />
                  </div>
                </div>
                {/* País */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">País</label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" checked={profileCountry === 'CL'} onChange={() => { setProfileCountry('CL'); setProfileData(p => ({ ...p, rut_profesional: '' })); }} className="text-[#2a9d8f]" />
                      <span className="text-sm">Chile</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" checked={profileCountry !== 'CL'} onChange={() => { setProfileCountry('OTHER'); setProfileData(p => ({ ...p, rut_profesional: '' })); }} className="text-[#2a9d8f]" />
                      <span className="text-sm">Otro</span>
                    </label>
                  </div>
                </div>
                {/* RUT */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{profileCountry === 'CL' ? 'RUT Profesional' : 'ID / CURP'}</label>
                  <div className="relative">
                    <IdCard className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input type="text" name="rut_profesional" value={profileData.rut_profesional} onChange={handleProfileChange}
                      className="w-full pl-10 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] bg-gray-50"
                      placeholder={profileCountry === 'CL' ? '12.345.678-9' : 'DNI o CURP'} />
                  </div>
                </div>
                {/* Registro */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">N° Registro Superintendencia</label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input type="text" name="rol_minsal" value={profileData.rol_minsal} onChange={handleProfileChange}
                      className="w-full pl-10 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] bg-gray-50" placeholder="123456" />
                  </div>
                </div>
                {/* Dirección y Ciudad */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dirección Consulta</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input type="text" name="direccion_consulta" value={profileData.direccion_consulta} onChange={handleProfileChange}
                        className="w-full pl-10 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] bg-gray-50" placeholder="Av. Ejemplo 123" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ciudad</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input type="text" name="ciudad_consulta" value={profileData.ciudad_consulta} onChange={handleProfileChange}
                        className="w-full pl-10 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] bg-gray-50" placeholder="Ej: Santiago" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button type="submit" disabled={isSaving}
                className="inline-flex items-center px-6 py-3 rounded-xl text-sm font-medium text-white bg-[#2A9D8F] hover:bg-[#21867a] disabled:opacity-50 transition-colors">
                {isSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</> : <><Save className="w-4 h-4 mr-2" />Guardar Perfil</>}
              </button>
            </div>
          </form>
        )}

        {/* ===== TAB: SERVICIOS ===== */}
        {activeTab === 'servicios' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Stethoscope className="w-5 h-5 mr-2 text-[#e76f51]" />
                  Catálogo de Tratamientos
                </h2>
                <p className="text-sm text-gray-500">{filteredTratamientos.length} servicio{filteredTratamientos.length !== 1 ? 's' : ''} registrado{filteredTratamientos.length !== 1 ? 's' : ''}</p>
              </div>
              <AddButton onClick={() => handleOpenTratamientoModal()}>Nuevo Tratamiento</AddButton>
            </div>

            {/* Tabla */}
            {loadingTratamientos ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#2A9D8F]" /></div>
            ) : (
              <ResponsiveTable
                data={filteredTratamientos}
                columns={tratamientoColumns}
                onEdit={handleOpenTratamientoModal}
                onDelete={(item) => handleDeleteTratamiento(item.id)}
                emptyMessage={formData.especialidad && formData.especialidad !== 'multi' ? `No hay tratamientos de ${formData.especialidad === 'podologia' ? 'podología' : formData.especialidad === 'cosmetologia' ? 'cosmetología' : 'manicuría'}. Crea tu primer servicio.` : "No hay tratamientos. Crea tu primer servicio."}
                keyField="id"
              />
            )}

            {/* Modal Tratamiento */}
            {showTratamientoModal && (
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center" onClick={(e) => e.target === e.currentTarget && handleCloseTratamientoModal()}>
                <div className="relative mx-4 p-6 border w-full max-w-md shadow-lg rounded-lg bg-white">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {editingTratamiento ? 'Editar Tratamiento' : 'Nuevo Tratamiento'}
                    </h3>
                    <button onClick={handleCloseTratamientoModal} className="text-gray-400 hover:text-gray-600">
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  {tratamientoFormError && (
                    <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">{tratamientoFormError}</div>
                  )}
                  <form onSubmit={handleSubmitTratamiento}>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nombre <span className="text-red-500">*</span></label>
                      <input type="text" value={tratamientoFormData.nombre} onChange={(e) => setTratamientoFormData({ ...tratamientoFormData, nombre: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F]" placeholder="Podología General" required />
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Precio <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                          <input type="number" value={tratamientoFormData.precio} onChange={(e) => setTratamientoFormData({ ...tratamientoFormData, precio: e.target.value })}
                            className="w-full pl-8 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F]" placeholder="15000" min="0" step="100" required />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Duración (min) <span className="text-red-500">*</span></label>
                        <input type="number" value={tratamientoFormData.duracion_minutos} onChange={(e) => setTratamientoFormData({ ...tratamientoFormData, duracion_minutos: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F]" placeholder="60" min="1" max="480" required />
                      </div>
                    </div>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Descripción <span className="text-gray-400">(opcional)</span></label>
                      <textarea value={tratamientoFormData.descripcion} onChange={(e) => setTratamientoFormData({ ...tratamientoFormData, descripcion: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] resize-none" rows="2" placeholder="Descripción breve..." />
                    </div>
                    <div className="flex justify-end gap-3">
                      <button type="button" onClick={handleCloseTratamientoModal} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg">Cancelar</button>
                      <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-[#2a9d8f] hover:bg-[#23877a] rounded-lg">
                        {editingTratamiento ? 'Guardar Cambios' : 'Crear'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfiguracionClinicaPage;
